/**
 * Live width of the table tile's scroll container — feeds
 * `computeFitColumnWidths` (`tileTableFitWidth.ts`) so a narrow tile's
 * default (never manually resized) columns shrink to fit instead of forcing
 * horizontal scroll for just 2-3 short columns.
 *
 * Uses a CALLBACK ref (not an effect over a ref object): the scroll container
 * only mounts once `QueryTileStates` leaves its skeleton/empty branches, and
 * can be remounted later without any state the component could list as effect
 * deps changing — an effect-based measurement then keeps observing a detached
 * element and the width stays stuck at 0 (this shipped as clipped "tota" /
 * "orders_cr" headers twice). With a callback ref, React itself reports every
 * attach/detach, so measurement and the ResizeObserver always target the
 * element actually in the DOM.
 */
import { useCallback, useRef, useState } from 'react';

export function useTileTableContainerWidth(): {
  attachRef: (el: HTMLDivElement | null) => void;
  width: number;
} {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const attachRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  return { attachRef, width };
}
